import { DealNextStep, DealStage, isDiligenceStage } from './deal'

/**
 * A company's status, calculated from its deals and the loan book; never hand-set.
 * Between deals a company sits in the Radar pool (New, Tracking, Past client).
 */
export enum CompanyLifecycleStatus {
  Radar = 'Radar',
  ActiveProspect = 'Active Prospect',
  DueDiligence = 'Due Diligence',
  Current = 'Current',
  Archived = 'Archived',
}

/** Radar and Archived are subdivided; the other three statuses are atomic. */
export enum CompanyLifecycleSubStatus {
  New = 'New',
  Tracking = 'Tracking',
  PastClient = 'Past Client',
  Rejected = 'Rejected',
  Defunct = 'Defunct',
}

export interface ICompanyLifecycle {
  status: CompanyLifecycleStatus
  subStatus: CompanyLifecycleSubStatus | null
}

export interface ILifecycleDerivationInput {
  /** client_status is Current. Owned by Portfolio; the pipeline never writes it. */
  isActiveClient: boolean
  /** client_status is Past or Past / Archived. */
  isPastClient: boolean
  /** Stage of the company's open deal; null when none is open. */
  openDealStage: DealStage | null
  /** What the most recently closed deal decided for the company; null if none closed. */
  latestNextStep: DealNextStep | null
}

/** Status an open deal at this stage gives its company. */
export const statusForStage = (stage: DealStage): CompanyLifecycleStatus =>
  isDiligenceStage(stage)
    ? CompanyLifecycleStatus.DueDiligence
    : CompanyLifecycleStatus.ActiveProspect

const NEXT_STEP_LIFECYCLE: Record<DealNextStep, ICompanyLifecycle> = {
  [DealNextStep.Tracking]: {
    status: CompanyLifecycleStatus.Radar,
    subStatus: CompanyLifecycleSubStatus.Tracking,
  },
  [DealNextStep.Rejected]: {
    status: CompanyLifecycleStatus.Archived,
    subStatus: CompanyLifecycleSubStatus.Rejected,
  },
  [DealNextStep.Defunct]: {
    status: CompanyLifecycleStatus.Archived,
    subStatus: CompanyLifecycleSubStatus.Defunct,
  },
}

/** Rules checked top to bottom; the first match wins. */
export const deriveCompanyLifecycle = ({
  isActiveClient,
  isPastClient,
  openDealStage,
  latestNextStep,
}: ILifecycleDerivationInput): ICompanyLifecycle => {
  if (isActiveClient) return { status: CompanyLifecycleStatus.Current, subStatus: null }
  if (openDealStage) return { status: statusForStage(openDealStage), subStatus: null }
  if (latestNextStep) return NEXT_STEP_LIFECYCLE[latestNextStep]
  return {
    status: CompanyLifecycleStatus.Radar,
    subStatus: isPastClient ? CompanyLifecycleSubStatus.PastClient : CompanyLifecycleSubStatus.New,
  }
}
